import { useEffect, useRef, useState } from 'react';

export const useOnScreen = ({
    options = { threshold: 0.5 },
    disconnectOnIntersection = true,
}: {
    options?: IntersectionObserverInit;
    disconnectOnIntersection?: boolean;
} = {}): [React.RefObject<HTMLDivElement>, boolean] => {
    const ref = useRef<HTMLDivElement>(null);
    const [isInView, setIsInView] = useState(false);

    // Use refs to avoid dependency issues
    const disconnectOnIntersectionRef = useRef(disconnectOnIntersection);
    const optionsRef = useRef(options);

    // Update refs when props change
    useEffect(() => {
        disconnectOnIntersectionRef.current = disconnectOnIntersection;
        optionsRef.current = options;
    }, [disconnectOnIntersection, options]);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            const { isIntersecting } = entries[0];

            setIsInView(isIntersecting);

            if (isIntersecting && disconnectOnIntersectionRef.current) {
                observer.disconnect();
            }
        }, optionsRef.current);

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            observer.disconnect();
        };
    }, []); // Empty dependency array - refs handle the dependencies

    return [ref, isInView];
};
